import { Overlay, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { placeAtTarget } from '../../util/overlay-position';

/** The panels place themselves with placeOverlay, the overlay only holds them. */
class ManualPositionStrategy implements PositionStrategy {
  attach(): void {}
  apply(): void {}
  dispose(): void {}
}

/**
 * A popup panel next to a target element, on a CDK overlay: `show(event,
 * target)`, `hide()`, `toggle()`, `overlayVisible`, `target` and the
 * `container` the callers of `opened` place with placeOverlay. Without such a
 * placement it opens at its target (placeAtTarget). A field marked with
 * autofocus gets the focus when it opens. A click outside the panel and its
 * target, Escape or scrolling the page close it.
 */
@Component({
  selector: 'app-overlay-panel',
  template: `<ng-template #panelTemplate>
    <div class="app-overlay-panel {{ styleClass }}">
      <div class="app-overlay-panel-content"><ng-content></ng-content></div>
    </div>
  </ng-template>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class OverlayPanelComponent implements OnDestroy {
  private overlay = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);

  @Input() styleClass = '';
  @Output() opened = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  @ViewChild('panelTemplate', { static: true })
  private panelTemplate!: TemplateRef<unknown>;

  public target: Element | null = null;
  private overlayRef: OverlayRef | null = null;
  private subscriptions = new Subscription();

  get overlayVisible(): boolean {
    return !!this.overlayRef?.hasAttached();
  }

  /** The element the panel is positioned by. */
  get container(): HTMLElement | undefined {
    return this.overlayVisible ? this.overlayRef?.overlayElement : undefined;
  }

  show(event: Event | null, target?: Element | null) {
    // The click that opens the panel is no outside click.
    event?.stopPropagation();
    this.target =
      target ?? ((event?.currentTarget as Element | null | undefined) || null);
    if (this.overlayVisible) {
      return;
    }
    const ref = (this.overlayRef ??= this.overlay.create({
      positionStrategy: new ManualPositionStrategy(),
      scrollStrategy: this.overlay.scrollStrategies.close(),
    }));
    ref.attach(new TemplatePortal(this.panelTemplate, this.viewContainerRef));
    placeAtTarget(ref.overlayElement, this.target);
    this.subscriptions = new Subscription();
    this.subscriptions.add(
      ref.outsidePointerEvents().subscribe((pointer) => {
        // Clicks into the drop downs of fields in the panel do not get here:
        // the CDK reports them only to the overlay above this one.
        const clicked = pointer.target as Element | null;
        if (!clicked || !this.target?.contains(clicked)) {
          this.hide();
        }
      })
    );
    this.subscriptions.add(
      ref
        .keydownEvents()
        .subscribe((key) => key.key === 'Escape' && this.hide())
    );
    this.subscriptions.add(ref.detachments().subscribe(() => this.detached()));
    this.opened.emit();
    // The content exists before the panel opens, so an autofocus in it would
    // be spent already; the panel gives the focus when it opens instead.
    ref.overlayElement
      .querySelector<HTMLElement>(
        '[cdkFocusInitial], [autofocus]:not([autofocus="false"])'
      )
      ?.focus();
  }

  hide() {
    if (this.overlayVisible) {
      this.overlayRef?.detach();
    }
  }

  toggle(event: Event | null, target?: Element | null) {
    const next =
      target ?? ((event?.currentTarget as Element | null | undefined) || null);
    if (this.overlayVisible && next === this.target) {
      this.hide();
    } else {
      this.hide();
      this.show(event, next);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.overlayRef?.dispose();
  }

  private detached() {
    this.subscriptions.unsubscribe();
    this.closed.emit();
  }
}
